/* The teardrop path is authored in this coordinate space */
const PIN_DESIGN_SIZE = 48;
/* Logical box size of the pin at scale 1.0 */
export const PIN_BOX_SIZE = 28;
/* Rendered at 2x so the marker stays crisp on retina at full zoom */
export const PIN_PIXEL_RATIO = 2;
const PIN_RASTER_SIZE = PIN_BOX_SIZE * PIN_PIXEL_RATIO;

/* Drawing the teardrop costs a canvas allocation, and both maps ask for the same handful of
   colours repeatedly (once per project on the landholding map), so keep one canvas per fill. */
const pinCanvasCache = new Map<string, HTMLCanvasElement | null>();

const drawPinCanvas = (fillHex: string): HTMLCanvasElement | null => {
  const canvas = document.createElement("canvas");
  canvas.width = PIN_RASTER_SIZE;
  canvas.height = PIN_RASTER_SIZE;

  const context = canvas.getContext("2d");
  if (!context) return null;

  /* Scale the design-space path up to the raster resolution */
  context.scale(PIN_RASTER_SIZE / PIN_DESIGN_SIZE, PIN_RASTER_SIZE / PIN_DESIGN_SIZE);

  const path = new Path2D(
    "M24 2 C14.6 2 7 9.6 7 19 C7 30.4 21.2 44.2 22.8 45.6 C23.5 46.2 24.5 46.2 25.2 45.6 C26.8 44.2 41 30.4 41 19 C41 9.6 33.4 2 24 2 Z"
  );

  context.fillStyle = fillHex;
  context.strokeStyle = "#ffffff";
  context.lineWidth = 3;
  context.fill(path);
  context.stroke(path);

  context.beginPath();
  context.arc(24, 19, 6.5, 0, Math.PI * 2);
  context.fillStyle = "#ffffff";
  context.fill();

  return canvas;
};

/**
 * Rasterises the shared map-pin marker in the given fill colour.
 *
 * Takes a hex string rather than a named palette entry because the two callers colour their pins
 * from different palettes: the dashboard from its own `DashboardMapPinColour` tones, the
 * landholding map from `ColorConstants.CHART_COLORS` so each pin matches its project's boundary.
 *
 * Mapbox wants `ImageData` for `addImage` (see the dashboard's `createPinImage`); OpenLayers wants
 * the canvas itself for `ol/style/Icon`'s `img`. Hence a canvas, which converts to either.
 */
export const createPinCanvas = (fillHex: string): HTMLCanvasElement | null => {
  const cached = pinCanvasCache.get(fillHex);
  if (cached !== undefined) return cached;

  const canvas = drawPinCanvas(fillHex);
  pinCanvasCache.set(fillHex, canvas);
  return canvas;
};
